import { FunctionComponent, useCallback, useTransition } from 'react'
import { useRouter } from 'next/navigation'
import { Route } from 'next'
import useIsMobile from '@/hooks/useIsMobile'
import TXT from '@/messages'
import { AnchorButton } from '../AnchorButton'

type Props = {
  site: SiteCardT
  siteCardVariant?: 'exhibition' | 'events'
  useNextRouter?: boolean
}

const {
  buttons: {
    landingPages: {
      events: eventsButton,
      eventsMobile: eventsMobileButton,
      exhibition: exhibitionButton,
      exhibitionMobile: exhibitionMobileButton,
    },
  },
} = TXT

/**
 * This is used on landing pages and the error page
 */
const VenueLinkBlock: FunctionComponent<Props> = ({
  site,
  siteCardVariant,
  useNextRouter = true,
}: Props) => {
  const router = useRouter()
  const [isPending, startTransition] = useTransition()

  const onClick = useCallback((href: string) => {
    if (useNextRouter) {
      router.push(`${href}${window.location.search}` as Route)
    } else {
      window.location.href = `${href}${window.location.search}`
    }
  }, [router, useNextRouter])

  const mobile = useIsMobile()

  const orgAndCity = `${TXT.mw} ${site.cityName}`

  return (
    <>
      {site.enabled.exhibition && (
        <AnchorButton
          title={TXT.eventsA11y.buyAdmissionTicketsFor(orgAndCity)}
          aria-label={TXT.eventsA11y.exhibitTickets(orgAndCity)}
          size="md"
          weight="semibold"
          onClick={() => startTransition(() => onClick(site.href.exhibition))}
          uppercase
          data-testid="exhibit-tickets-cta"
          disabled={isPending}
        >
          {mobile
            ? exhibitionMobileButton
            : exhibitionButton}
        </AnchorButton>
      )}
      {site.enabled.events && (
        <AnchorButton
          title={TXT.eventsA11y.buyTicketsFor(orgAndCity)}
          aria-label={TXT.eventsA11y.eventTickets(orgAndCity)}
          size="md"
          weight="semibold"
          variant={siteCardVariant === 'exhibition' ? 'inverse' : 'default'}
          onClick={() => startTransition(() => onClick(site.href.events))}
          uppercase
          data-testid="event-tickets-cta"
          disabled={isPending}
        >
          {(mobile && siteCardVariant !== 'events')
            ? eventsMobileButton
            : eventsButton}
        </AnchorButton>
      )}
    </>
  )
}

export default VenueLinkBlock
